import { useMemo } from 'react';

import useGetEvent from 'features/common/hooks/useGetEvent';
import { useGetSeatsIOEvent } from 'hooks/useGetSeatsIOEvent';
import {
	buildSeatsCategoryColorMap,
	buildTicketTypeColorMap,
	TICKET_TYPE_FALLBACK_COLOR,
} from 'features/common/utils/ticketTypeColors';

export interface TicketTypeColors {
	/** ticket-type name → display colour (its stored colour, or a stable palette fallback). */
	byName: Record<string, string>;
	/**
	 * The display colour for a ticket type by name: its stored colour, a stable palette fallback when
	 * unset, or neutral grey for an unknown name (e.g. a deleted type still on an old order row).
	 */
	resolveByName: (name: string | null | undefined) => string;
}

/**
 * The single hook for a ticket type's display colour anywhere in the iframe. Reads the event's ticket
 * types from the shared `['event', id]` React Query cache (no extra request) and resolves each name to
 * its stored colour or a stable palette fallback via `buildTicketTypeColorMap`.
 *
 * Used by the ticket-type table tags (Transactions, Comped, Attendee List, Scanned), the pool member
 * chips, the ticket-type tile rails, and the colour-picker auto preview — so a tier reads the SAME
 * colour on every page. Resolving here (not from each endpoint's per-row colour) also makes the colour
 * consistent even when an endpoint omits/null-fills `ticket_type_color`.
 */
export function useTicketTypeColors(): TicketTypeColors {
	const { event } = useGetEvent();
	// Seated events: the bound seats.io category colour is the display source of truth, so the iframe
	// stays uniform with the seats chart. An undefined key (non-seated) fires no fetch; while the seats
	// query is loading/errored/unreachable the overlay is empty and resolution falls through to the
	// stored colour → palette. The query key is shared, so this dedupes with the seats section's fetch.
	const { data: seatsEvent } = useGetSeatsIOEvent(event?.seatsEventKey);
	const seatsOverlay = useMemo(
		() => buildSeatsCategoryColorMap(event?.ticketTypes ?? [], seatsEvent?.categories),
		[event?.ticketTypes, seatsEvent?.categories]
	);
	const byName = useMemo(
		() => buildTicketTypeColorMap(event?.ticketTypes ?? [], seatsOverlay),
		[event?.ticketTypes, seatsOverlay]
	);

	return useMemo(
		() => ({
			byName,
			resolveByName: (name) => (name ? byName[name] : undefined) || TICKET_TYPE_FALLBACK_COLOR,
		}),
		[byName]
	);
}
